import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Volume2, Mic, Sparkles } from "lucide-react";

interface VoiceModeInfoProps {
  isOpen: boolean;
  onClose: () => void;
}

export const VoiceModeInfo: React.FC<VoiceModeInfoProps> = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Volume2 className="h-6 w-6 text-primary" />
            Introducing Voice Mode
          </DialogTitle>
          <DialogDescription className="text-base space-y-4 pt-4">
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-4 rounded-lg border border-primary/20">
              <p className="text-foreground font-medium mb-2">
                Experience therapy in a whole new way with Amanda's AI voice
              </p>
              <p className="text-muted-foreground text-sm">
                Powered by ElevenLabs' advanced voice technology, Therappi<sup className="text-[0.6em]">®</sup> can now speak responses 
                in a warm, empathetic voice designed to create a more natural reflective conversation.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="bg-primary/10 p-2 rounded-full mt-1">
                  <Mic className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="font-semibold mb-1">Voice Input (Already Active)</div>
                  <p className="text-sm text-muted-foreground">
                    Click the microphone button to speak your thoughts. Your words are transcribed 
                    and sent to Therappi just like typed messages.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-primary/10 p-2 rounded-full mt-1">
                  <Volume2 className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="font-semibold mb-1">Voice Output (Enable Now)</div>
                  <p className="text-sm text-muted-foreground">
                    When Voice Mode is ON, every AI response will be spoken aloud in Amanda's 
                    therapeutic voice. You can still read the text while listening.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-primary/10 p-2 rounded-full mt-1">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="font-semibold mb-1">Natural Conversations</div>
                  <p className="text-sm text-muted-foreground">
                    Speak and listen for a more immersive reflective experience. Perfect for 
                    when you want to relax, close your eyes, or need hands-free support.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-lg text-xs text-muted-foreground">
              <p className="mb-2">
                <strong className="text-foreground">Note:</strong> Voice responses use the same 
                3 credits per exchange as text conversations. Audio generation takes 2-5 seconds.
              </p>
              <p>
                Toggle Voice Mode ON/OFF anytime using the button at the top of the chat.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end pt-4">
          <Button onClick={onClose} className="gap-2">
            Got it, let's try!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
